/**
 * Shared fetch helpers for kiosk reliability.
 *
 * Kiosks run unattended, so every network call must fail fast instead of
 * hanging forever on a dead/slow connection. `fetchWithTimeout` aborts the
 * request after `timeoutMs`; `fetchWithRetry` adds a few automatic retries for
 * transient failures on critical flows (payment, AI generation, etc.).
 */

export const DEFAULT_TIMEOUT_MS = 30000;

export interface FetchWithTimeoutOptions extends RequestInit {
  /** Abort the request after this many ms. Defaults to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number;
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  options: FetchWithTimeoutOptions = {}
): Promise<Response> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, signal, ...init } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // Respect a caller-provided signal in addition to our timeout.
  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error: any) {
    if (error?.name === "AbortError") {
      throw new Error("Request timed out. Please check the connection and try again.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export interface FetchWithRetryOptions extends FetchWithTimeoutOptions {
  /** Number of additional attempts after the first. Defaults to 2. */
  retries?: number;
  /** Base delay between attempts in ms (grows linearly). Defaults to 800. */
  retryDelayMs?: number;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchWithRetry(
  input: RequestInfo | URL,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const { retries = 2, retryDelayMs = 800, ...rest } = options;

  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetchWithTimeout(input, rest);
      // Retry only on server errors; client errors won't get better on retry.
      if (response.status >= 500 && attempt < retries) {
        lastError = new Error(`HTTP ${response.status}`);
        await wait(retryDelayMs * (attempt + 1));
        continue;
      }
      return response;
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await wait(retryDelayMs * (attempt + 1));
        continue;
      }
    }
  }

  throw lastError instanceof Error ? lastError : new Error("Network request failed");
}
